import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Package, Truck, MapPin, Clock, CheckCircle2, Home } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrackingPage() {
  const { trackingNumber } = useParams();
  const [trackingData, setTrackingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrackingData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/tracking/${trackingNumber}`);
      setTrackingData(response.data);
      setError(null);
    } catch (err) {
      setError("Tracking number not found");
    } finally {
      setIsLoading(false);
    }
  }, [trackingNumber]);

  useEffect(() => {
    if (trackingNumber) {
      fetchTrackingData();
    }
  }, [trackingNumber, fetchTrackingData]);

  const getStatusColor = (status) => {
    const map = {
      requested: "bg-blue-100 text-blue-800",
      quoted: "bg-purple-100 text-purple-800",
      booked: "bg-yellow-100 text-yellow-800",
      in_transit: "bg-cyan-100 text-cyan-800",
      delivered: "bg-green-100 text-green-800"
    };
    return map[status] || "bg-gray-100 text-gray-800";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <p className="text-zinc-400">Loading tracking information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-zinc-900 rounded-sm border border-zinc-800 p-8 text-center">
          <Package className="h-16 w-16 text-zinc-600 mx-auto mb-6" />
          <h1 className="font-heading text-2xl font-bold uppercase text-white mb-4">Tracking Not Found</h1>
          <p className="text-zinc-400 mb-6">Tracking: {trackingNumber}</p>
          <Link to="/"><Button className="bg-blue-600 hover:bg-blue-700"><Home className="h-4 w-4 mr-2" /> Home</Button></Link>
        </div>
      </div>
    );
  }

  const data = trackingData;
  const events = data?.events || [];

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <header className="py-8 px-6 border-b border-zinc-800">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="text-zinc-400 hover:text-white text-sm mb-4 inline-flex items-center gap-2">
            <Home className="h-4 w-4" /> Back
          </Link>
          <h1 className="font-heading text-4xl font-bold uppercase mt-4">Shipment Tracking</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-zinc-900 rounded-sm border border-zinc-800 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-zinc-500 text-sm uppercase mb-1">Tracking Number</p>
              <p className="font-mono text-2xl font-bold">{data.tracking_number}</p>
            </div>
            <Badge className={getStatusColor(data.status) + " text-sm uppercase"}>
              {data.status?.replace("_", " ")}
            </Badge>
          </div>

          {data.order_number && (
            <div className="mb-6 pb-6 border-b border-zinc-800">
              <p className="text-zinc-500 text-sm">Order Reference</p>
              <p className="font-mono">{data.order_number}</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4">
              <MapPin className="h-5 w-5 text-zinc-400 mt-1" />
              <div>
                <p className="text-zinc-500 text-sm">From</p>
                <p className="font-medium">{data.origin}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="h-5 w-5 text-blue-600 mt-1" />
              <div>
                <p className="text-zinc-500 text-sm">To</p>
                <p className="font-medium">{data.destination}</p>
              </div>
            </div>
          </div>

          {(data.carrier || data.estimated_days) && (
            <div className="mt-6 pt-6 border-t border-zinc-800 grid md:grid-cols-2 gap-6">
              {data.carrier && <div><p className="text-zinc-500 text-sm">Carrier</p><p className="font-medium">{data.carrier}</p></div>}
              {data.estimated_days && <div><p className="text-zinc-500 text-sm">Transit Time</p><p className="font-medium">{data.estimated_days} days</p></div>}
            </div>
          )}
        </div>

        {events.length > 0 ? (
          <div className="bg-zinc-900 rounded-sm border border-zinc-800 p-8">
            <h2 className="font-heading text-xl font-bold uppercase mb-6">Tracking History</h2>
            <div className="space-y-6">
              {events.map((event, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-4 h-4 rounded-full ${idx === 0 ? 'bg-blue-600' : 'bg-zinc-700'}`}></div>
                    {idx < events.length - 1 && <div className="w-0.5 h-full bg-zinc-700 mt-2"></div>}
                  </div>
                  <div className="flex-1 pb-6">
                    <p className="font-medium">{event.status}</p>
                    <p className="text-zinc-400 text-sm mt-1">{event.location}</p>
                    <p className="text-zinc-500 text-xs mt-1">{event.timestamp}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-zinc-900 rounded-sm border border-zinc-800 p-12 text-center">
            <Clock className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="font-heading text-lg uppercase text-zinc-400 mb-2">No Updates Yet</h3>
            <p className="text-sm text-zinc-500">Events will appear once the shipment is in transit.</p>
          </div>
        )}
      </main>
    </div>
  );
}
